// src/lib/api/employerChatApi.ts
//
// CRUD client for persistent V2 chat sessions. Scoped to the calling
// employer's user — no user_id args needed (server resolves from JWT).

import { getApiClient } from './client';

// One row in the rail. Slim — no message body.
export interface EmployerChat {
    id: string;
    title: string;
    provider: string;
    model: string;
    message_count: number;
    total_tokens: number;
    last_active_at: string;
    is_pinned: boolean;
    archived_at: string | null;
    created_at: string;
}

// Full chat with the persisted conversation. ai.Message is the
// framework's wire shape — role + content + optional tool_calls
// + optional name/tool_call_id for tool-result messages. We keep
// it loose (`unknown[]` for things we don't render yet) to
// future-proof: the framework can add fields without breaking us.
export interface EmployerChatMessage {
    role: 'system' | 'user' | 'assistant' | 'tool';
    content: string;
    name?: string;
    tool_call_id?: string;
    tool_calls?: unknown[];
}

export interface EmployerChatDetail extends EmployerChat {
    messages: EmployerChatMessage[];
    /** #318 — `messages` may be a WINDOW onto the end of a long chat. These say
     *  how much of the transcript exists and whether anything sits above the
     *  window, so "you are not seeing all of it" is stated rather than guessed.
     *  Never reflects what Link is SENT — the model always gets everything. */
    total_messages?: number;
    has_more?: boolean;
    /** Where a non-destructive compaction folded the history, as an index into
     *  the FULL transcript — translate with (total_messages - messages.length)
     *  before using it against `messages`. Lets a reopened chat draw the same
     *  divider a live compaction streams; without it, a fold that happened
     *  while nobody was streaming (auto-compaction, or R10's on-save fold) left
     *  no trace at all. 0 / absent means no compaction is active. */
    compacted_through?: number;
    compaction_summary?: string;
}

const list = async (includeArchived = false): Promise<EmployerChat[]> => {
    const path = includeArchived
        ? '/api/employer-chats?include_archived=true'
        : '/api/employer-chats';
    return getApiClient().get<EmployerChat[]>(path);
};

// turns limits how many recent EXCHANGES are hydrated (#318). Omitted = the
// backend default; pass 0 for the whole transcript, which is what "load older"
// asks for.
const get = async (id: string, turns?: number): Promise<EmployerChatDetail> => {
    const q = turns === undefined ? '' : `?turns=${turns}`;
    return getApiClient().get<EmployerChatDetail>(`/api/employer-chats/${id}${q}`);
};

// Chats whose transcript references `marker` (e.g. "module_id=<id>" /
// "need_id=<id>") — used by "Redigera med Link" to find an existing chat
// already working on that entity. Most-recent / pinned first.
const listReferencing = async (marker: string): Promise<EmployerChat[]> => {
    return getApiClient().get<EmployerChat[]>(`/api/employer-chats?ref=${encodeURIComponent(marker)}`);
};

const rename = async (id: string, title: string): Promise<void> => {
    return getApiClient().patch<void>(`/api/employer-chats/${id}/title`, { title });
};

const archive = async (id: string): Promise<void> => {
    return getApiClient().patch<void>(`/api/employer-chats/${id}/archived`, {});
};

const unarchive = async (id: string): Promise<void> => {
    return getApiClient().patch<void>(`/api/employer-chats/${id}/unarchived`, {});
};

const setPinned = async (id: string, isPinned: boolean): Promise<void> => {
    return getApiClient().patch<void>(`/api/employer-chats/${id}/pinned`, { is_pinned: isPinned });
};

const remove = async (id: string): Promise<void> => {
    return getApiClient().delete<void>(`/api/employer-chats/${id}`);
};

export const employerChatApi = {
    list,
    listReferencing,
    get,
    rename,
    archive,
    unarchive,
    setPinned,
    remove,
};
